import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Footprints, Dumbbell, Repeat, Gauge } from 'lucide-react';
import { Reveal } from '../hooks/useReveal';
import PageHero from '../components/PageHero';
import TrialCTA from '../components/TrialCTA';

const PILIERS = [
  { icon: Footprints, title: 'La course (le socle)', text: "8 km au total : c'est la base. Un programme Hyrox réussi se construit d'abord sur une solide aisance en course à pied, y compris fatigué." },
  { icon: Dumbbell, title: 'La force fonctionnelle', text: 'Sled push/pull, farmers carry, wall balls : il faut de la force-endurance sur les jambes, le dos et le grip — pas du maxi développé couché.' },
  { icon: Repeat, title: "L'endurance musculaire", text: 'Répéter un effort de force sans s\'écrouler : le vrai déterminant de la perf Hyrox. On l\'entraîne en circuits et en intervalles longs.' },
  { icon: Gauge, title: 'Les transitions (Roxzone)', text: "Passer de la course à la station et inversement sans exploser : le « compromis » qui se travaille spécifiquement et fait gagner des minutes." },
];

const SEMAINE = [
  { j: 'Course', d: '1 séance d\'endurance fondamentale (40-60 min) + 1 séance d\'intervalles (ex. 8 × 400 m). La base aérobie.' },
  { j: 'Force', d: '2 séances : bas du corps lourd (squat, deadlift, fentes) et tirage/grip. La machine qui pousse le sled.' },
  { j: 'Hybride / stations', d: '1 séance « compromise running » : course + station enchaînées, façon course réelle. Le cœur de la prépa.' },
  { j: 'Récup active', d: 'Mobilité, gainage, sommeil. La progression se construit aussi à la récupération.' },
];

const PHASES = [
  { n: 'Base', d: 'Construire le moteur aérobie et la technique des mouvements. Volume modéré, intensité basse à moyenne.' },
  { n: 'Build', d: 'Monter l\'intensité : intervalles, circuits force-endurance, premières simulations de stations.' },
  { n: 'Peak', d: 'Spécifique course : simulations Hyrox complètes ou partielles, travail du pacing et des transitions.' },
  { n: 'Taper', d: '7-10 jours avant la course : on réduit le volume, on garde un peu d\'intensité, on arrive frais.' },
];

export default function ProgrammeHyrox() {
  return (
    <>
      <PageHero
        eyebrow="La préparation"
        title="Le programme"
        accent="d'entraînement Hyrox"
        img="functional"
        subtitle="Comment s'entraîner pour une Hyrox : les piliers de la prépa, la semaine type, les phases (base, build, peak, taper) — et comment on te coache, à Toulouse et Colomiers."
      />

      <section className="py-14 md:py-28 max-w-7xl mx-auto px-5 lg:px-8">
        <Reveal className="mb-12">
          <p className="hx-eyebrow mb-4">Les fondations</p>
          <h2 className="font-display text-4xl md:text-6xl leading-[0.95]">Les 4 piliers d'un programme Hyrox</h2>
        </Reveal>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {PILIERS.map((p, i) => (
            <Reveal key={p.title} delay={i * 90}>
              <div className="hx-card rounded-md p-7 h-full">
                <div className="w-12 h-12 grid place-items-center bg-accent/10 border border-accent/30 rounded-md mb-5">
                  <p.icon size={22} className="text-accent" />
                </div>
                <h3 className="font-cond text-lg tracking-wide mb-2">{p.title}</h3>
                <p className="text-[var(--hx-muted)] text-sm leading-relaxed">{p.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="py-14 md:py-28 bg-[#0d0d0d]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal className="mb-12">
            <p className="hx-eyebrow mb-4">La semaine type</p>
            <h2 className="font-display text-4xl md:text-6xl leading-[0.95]">À quoi ressemble une semaine</h2>
            <p className="text-[var(--hx-muted)] mt-5 max-w-2xl leading-relaxed">
              4 à 5 séances par semaine suffisent pour bien progresser. L'équilibre course / force / hybride est la
              clé — un programme Hyrox n'est ni du pur running, ni de la pure muscu.
            </p>
          </Reveal>
          <div className="space-y-3">
            {SEMAINE.map((s, i) => (
              <Reveal key={s.j} delay={(i % 4) * 60}>
                <div className="hx-card group rounded-md flex items-center gap-5 p-4 md:p-5">
                  <span className="font-display text-2xl md:text-3xl text-accent w-32 shrink-0">{s.j}</span>
                  <p className="text-[var(--hx-muted)] text-sm md:text-base flex-1">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 md:py-28 max-w-7xl mx-auto px-5 lg:px-8">
        <Reveal className="mb-12">
          <p className="hx-eyebrow mb-4">La périodisation</p>
          <h2 className="font-display text-4xl md:text-6xl leading-[0.95]">Les 4 phases vers ta course</h2>
        </Reveal>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {PHASES.map((p, i) => (
            <Reveal key={p.n} delay={i * 90}>
              <div className="hx-card rounded-md p-7 h-full">
                <span className="font-display text-3xl text-accent">{String(i + 1).padStart(2, '0')}</span>
                <h3 className="font-cond text-lg tracking-wide mt-3 mb-2">{p.n}</h3>
                <p className="text-[var(--hx-muted)] text-sm leading-relaxed">{p.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-12">
          <div className="hx-card rounded-md p-7 md:p-10">
            <h3 className="font-display text-2xl md:text-3xl mb-3">Débutant ou confirmé : on adapte</h3>
            <p className="text-[var(--hx-muted)] leading-relaxed max-w-3xl">
              Tu pars de zéro ? On bâtit d'abord ton aisance en course et la technique des stations. Tu vises un
              chrono ? On affine le pacing et les transitions. Dans les deux cas, le plus dur en autonomie, c'est de
              doser : trop de course, pas assez de force, ou l'inverse. C'est exactement là qu'un coach change tout —
              et c'est ce qu'on fait à Hybrid Training Club, à <Link to="/club/toulouse" className="text-accent hover:underline">Toulouse</Link> et
              à <Link to="/club/colomiers" className="text-accent hover:underline">Colomiers</Link>.
            </p>
            <Link to="/reserver" className="hx-btn mt-7">Préparer ma Hyrox, coaché <ArrowRight size={18} /></Link>
          </div>
        </Reveal>
      </section>

      <TrialCTA />
    </>
  );
}
